import React from 'react';

import { imgUser } from '@/assets/images';
import { Stack } from '@/components/layout';
import { Avatar, SvgIcon, Text } from '@/components/ui';
import { createStyles, useScaling } from '@/hooks';
import { colors, withOpacity } from '@/theme/colors';

type OnboardingAvatarProps = {
  /** Current avatar image. When omitted, the default placeholder circle is shown. */
  source?: React.ComponentProps<typeof Avatar.Image>['source'];
  /** Tapping the camera badge / avatar opens the image picker. */
  onPress?: () => void;
  /** Validation error message shown under the avatar (red, matches Figma). */
  errorMessage?: string;
  /** Whether to render the error ring. Defaults to `true` when `errorMessage` is set. */
  hasError?: boolean;
  /** Avatar border color. Defaults to the error color when in error, otherwise white. */
  borderColor?: string;
  /** Avatar border width in px. Defaults to 2 when in error, otherwise 1. */
  borderWidth?: number;
  /** Hide the camera badge (e.g. read-only "view player" mode). Defaults to `false`. */
  hideBadge?: boolean;
};

/** Figma: avatar placeholder circle fill (no stroke). */
const AVATAR_PLACEHOLDER_BG = '#e2e8f0';

/**
 * 120px profile avatar with a camera badge in the bottom-right corner, used at the
 * top of onboarding step 1 to let the guardian set their photo. On a validation
 * error the circle border turns red and the message is shown below (Figma). The
 * border color/width can be overridden via `borderColor` / `borderWidth`.
 */
export function OnboardingAvatar({
  source,
  onPress,
  errorMessage,
  hasError = Boolean(errorMessage),
  borderColor,
  borderWidth,
  hideBadge = false,
}: OnboardingAvatarProps) {
  const { scale } = useScaling();
  const styles = useStyles();

  const resolvedBorderColor = borderColor ?? (hasError ? colors.Error : 'white');
  const resolvedBorderWidth = borderWidth ?? (hasError ? scale(2) : 1);

  return (
    <Stack align="center" gap={scale(4)}>
      <Stack style={styles.avatarBox}>
        <Avatar
          size={scale(120)}
          borderColor={resolvedBorderColor}
          borderWidth={resolvedBorderWidth}
          onPress={onPress}>
          <Avatar.Image
            size={!source ? scale(60) : undefined}
            source={source || imgUser}
            disableCircular={!source}
            backgroundColor={AVATAR_PLACEHOLDER_BG}
          />
        </Avatar>

        {hideBadge ? null : (
          <Stack pointerEvents="none" style={styles.badge} align="center" justify="center">
            <SvgIcon name="IcCamera" size={scale(16)} color={colors.Blue} />
          </Stack>
        )}
      </Stack>

      {errorMessage ? (
        <Text variant="Font Guardian App/Subtitle/Subtitle 10 Regular" style={styles.errorText}>
          {errorMessage}
        </Text>
      ) : null}
    </Stack>
  );
}

const useStyles = createStyles(({ scale }) => ({
  avatarBox: {
    width: scale(120),
    height: scale(120),
  },
  badge: {
    position: 'absolute',
    right: 0,
    bottom: 0,
    width: scale(32),
    height: scale(32),
    borderRadius: scale(16),
    backgroundColor: withOpacity(colors['0'], 1),
  },
  errorText: {
    color: colors.Error,
    textAlign: 'center',
  },
}));

export default OnboardingAvatar;
